import { useState } from "react";
import type { ReactNode } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./button";
import { cn } from "./cn";
import { Logo } from "./logo";
import { UiLink } from "./router";
import { ThemeToggle } from "./theme-toggle";

/**
 * The frame the signed-out home page renders inside — the design's marketing
 * site header and footer, which have no counterpart in the dashboard shell.
 *
 * It is a component rather than a route layout because this app has exactly
 * one marketing page: `/` renders this when there is no session and is handed
 * `AppShell` when there is, and a router-level layout cannot see the session.
 *
 * Like the rest of `src/ui/`, it takes no data. The footer's link table is the
 * design's, minus the entries this app has no route for — the same rule
 * `nav.ts` follows, and for the same reason: `UiLink` hands `href` to the
 * router's untyped `to`, so a wrong entry is a dead link at click time.
 */

const sections = [
  { href: "#how-it-works", label: "How it works" },
  { href: "#security", label: "Security" },
];

/** `/sign-in` declares both of its search parameters, so a link that omits
 *  them has to say so explicitly — the router builds the href from what it is
 *  given, not from what the current URL happens to carry. */
const SIGN_IN_SEARCH = { next: undefined, client_id: undefined };

const footerColumns: Array<{
  title: string;
  links: Array<{ href: string; label: string; search?: Record<string, unknown> }>;
}> = [
  {
    title: "Account",
    links: [
      { href: "/sign-in", label: "Sign in", search: SIGN_IN_SEARCH },
      { href: "/register", label: "Create an account" },
    ],
  },
  {
    title: "Portal",
    links: [
      { href: "/wallets", label: "Wallets" },
      { href: "/usage", label: "Usage" },
    ],
  },
  {
    title: "Security",
    links: [
      { href: "/security", label: "Profile & security" },
      { href: "/device-approvals", label: "Device approvals" },
    ],
  },
];

export function MarketingShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-surface">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-100 focus:rounded-field focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-raised"
      >
        Skip to content
      </a>
      <SiteHeader />
      <main id="main">{children}</main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-surface/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-4 px-5 sm:px-8">
        <UiLink href="/" className="flex shrink-0 items-center gap-2.5">
          <Logo className="size-9" />
          <span className="font-display text-md font-bold tracking-[-0.01em] text-ink">
            Empite Suite
          </span>
        </UiLink>

        <nav className="ml-6 hidden items-center gap-6 md:flex" aria-label="Main">
          {sections.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-base font-medium text-ink-3 transition-colors hover:text-ink"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <ThemeToggle className="hidden sm:inline-flex" />
          <UiLink
            href="/sign-in"
            search={SIGN_IN_SEARCH}
            className="hidden rounded-field px-3 py-2 text-base font-semibold text-ink-2 transition-colors hover:text-ink sm:block"
          >
            Sign in
          </UiLink>
          <Button href="/register" className="hidden sm:inline-flex">
            Create an account
          </Button>
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            className="grid size-9 place-items-center rounded-field text-ink-2 hover:bg-surface-sunken sm:hidden"
          >
            {open ? <X className="size-4.5" aria-hidden /> : <Menu className="size-4.5" aria-hidden />}
          </button>
        </div>
      </div>

      <div
        className={cn(
          "overflow-hidden border-t border-line transition-[max-height] duration-300 ease-out-soft sm:hidden",
          open ? "max-h-80" : "max-h-0 border-t-0",
        )}
      >
        <div className="space-y-1 px-5 py-4">
          {sections.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="block rounded-field px-3 py-2.5 text-base font-medium text-ink-2 hover:bg-surface-sunken"
            >
              {link.label}
            </a>
          ))}
          <div className="flex items-center justify-between pt-2">
            <ThemeToggle />
          </div>
          <div className="grid gap-2 pt-2">
            <Button href="/register" size="lg">
              Create an account
            </Button>
            <Button href="/sign-in" search={SIGN_IN_SEARCH} variant="secondary" size="lg">
              Sign in
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-line">
      <div className="mx-auto max-w-[1280px] px-5 py-12 sm:px-8">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.5fr_repeat(3,1fr)]">
          <div>
            <div className="flex items-center gap-2.5">
              <Logo className="size-8" />
              <span className="font-display text-base font-bold text-ink">Empite Suite</span>
            </div>
            <p className="mt-3 max-w-xs text-xs text-ink-3">
              One account for every application in the suite. Each application keeps its own
              wallet and its own usage record.
            </p>
          </div>

          {footerColumns.map((column) => (
            <div key={column.title}>
              <p className="text-2xs font-bold tracking-[0.1em] text-ink-4 uppercase">
                {column.title}
              </p>
              <ul className="mt-3 space-y-2">
                {column.links.map((link) => (
                  <li key={link.href}>
                    <UiLink
                      href={link.href}
                      search={link.search}
                      className="text-xs font-medium text-ink-3 transition-colors hover:text-ink"
                    >
                      {link.label}
                    </UiLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-line-soft pt-6">
          <p className="text-2xs text-ink-4">© 2026 Empite. All rights reserved.</p>
          <p className="text-2xs text-ink-4">
            Demo environment — no real payments are taken.
          </p>
        </div>
      </div>
    </footer>
  );
}
